export type AccountClass = 'Asset' | 'Liability' | 'Equity' | 'Income' | 'Expense'
export type AccountKind = 'Header' | 'Control' | 'Posting' | 'Bank' | 'Cash' | 'Receivable' | 'Payable'
export type VoucherStatus = 'Draft' | 'Pending approval' | 'Posted' | 'Rejected'

export interface GlAccount {
  id: string
  code: string
  name: string
  parentId?: string
  level: number
  accountClass: AccountClass
  kind: AccountKind
  balance: number
  normalBalance: 'Debit' | 'Credit'
  currency: string
  active: boolean
  mandatoryDimension?: 'Cost center' | 'Project' | 'Department'
}

export interface VoucherLine {
  id: string
  accountCode: string
  accountName: string
  costCenter: string
  project: string
  department: string
  reference: string
  debit: number
  credit: number
  remarks: string
}

export interface VoucherRegisterItem {
  number: string
  type: string
  date: string
  narration: string
  source: string
  amount: number
  status: VoucherStatus
  owner: string
}

export const glAccounts: GlAccount[] = [
  { id: 'assets', code: '1000', name: 'Assets', level: 0, accountClass: 'Asset', kind: 'Header', balance: 0, normalBalance: 'Debit', currency: 'PKR', active: true },
  { id: 'current-assets', code: '1100', name: 'Current Assets', parentId: 'assets', level: 1, accountClass: 'Asset', kind: 'Header', balance: 0, normalBalance: 'Debit', currency: 'PKR', active: true },
  { id: 'cash', code: '1110', name: 'Cash & Cash Equivalents', parentId: 'current-assets', level: 2, accountClass: 'Asset', kind: 'Header', balance: 0, normalBalance: 'Debit', currency: 'PKR', active: true },
  { id: 'main-cash', code: '111001', name: 'Main Cash', parentId: 'cash', level: 3, accountClass: 'Asset', kind: 'Cash', balance: 12840000, normalBalance: 'Debit', currency: 'PKR', active: true, mandatoryDimension: 'Cost center' },
  { id: 'petty-cash', code: '111002', name: 'Petty Cash', parentId: 'cash', level: 3, accountClass: 'Asset', kind: 'Cash', balance: 465000, normalBalance: 'Debit', currency: 'PKR', active: true },
  { id: 'bank', code: '1120', name: 'Bank Accounts', parentId: 'current-assets', level: 2, accountClass: 'Asset', kind: 'Header', balance: 0, normalBalance: 'Debit', currency: 'PKR', active: true },
  { id: 'hbl', code: '112001', name: 'HBL Operating Account', parentId: 'bank', level: 3, accountClass: 'Asset', kind: 'Bank', balance: 42860000, normalBalance: 'Debit', currency: 'PKR', active: true },
  { id: 'receivables', code: '1130', name: 'Trade & Patient Receivables', parentId: 'current-assets', level: 2, accountClass: 'Asset', kind: 'Receivable', balance: 18720000, normalBalance: 'Debit', currency: 'PKR', active: true, mandatoryDimension: 'Department' },
  { id: 'liabilities', code: '2000', name: 'Liabilities', level: 0, accountClass: 'Liability', kind: 'Header', balance: 0, normalBalance: 'Credit', currency: 'PKR', active: true },
  { id: 'current-liabilities', code: '2100', name: 'Current Liabilities', parentId: 'liabilities', level: 1, accountClass: 'Liability', kind: 'Header', balance: 0, normalBalance: 'Credit', currency: 'PKR', active: true },
  { id: 'payables', code: '2110', name: 'Trade Payables', parentId: 'current-liabilities', level: 2, accountClass: 'Liability', kind: 'Payable', balance: 10480000, normalBalance: 'Credit', currency: 'PKR', active: true, mandatoryDimension: 'Cost center' },
  { id: 'payroll-payable', code: '2120', name: 'Payroll Liabilities', parentId: 'current-liabilities', level: 2, accountClass: 'Liability', kind: 'Control', balance: 5280000, normalBalance: 'Credit', currency: 'PKR', active: true },
  { id: 'equity', code: '3000', name: 'Equity', level: 0, accountClass: 'Equity', kind: 'Header', balance: 0, normalBalance: 'Credit', currency: 'PKR', active: true },
  { id: 'retained-earnings', code: '3100', name: 'Retained Earnings', parentId: 'equity', level: 1, accountClass: 'Equity', kind: 'Posting', balance: 77460000, normalBalance: 'Credit', currency: 'PKR', active: true },
  { id: 'income', code: '4000', name: 'Revenue', level: 0, accountClass: 'Income', kind: 'Header', balance: 0, normalBalance: 'Credit', currency: 'PKR', active: true },
  { id: 'patient-income', code: '4100', name: 'Patient Service Revenue', parentId: 'income', level: 1, accountClass: 'Income', kind: 'Posting', balance: 548000000, normalBalance: 'Credit', currency: 'PKR', active: true, mandatoryDimension: 'Department' },
  { id: 'other-income', code: '4200', name: 'Other Operating Income', parentId: 'income', level: 1, accountClass: 'Income', kind: 'Posting', balance: 14200000, normalBalance: 'Credit', currency: 'PKR', active: true },
  { id: 'expense', code: '5000', name: 'Expenses', level: 0, accountClass: 'Expense', kind: 'Header', balance: 0, normalBalance: 'Debit', currency: 'PKR', active: true },
  { id: 'clinical-expense', code: '5100', name: 'Clinical Services Expense', parentId: 'expense', level: 1, accountClass: 'Expense', kind: 'Header', balance: 0, normalBalance: 'Debit', currency: 'PKR', active: true },
  { id: 'medical-supplies', code: '5110', name: 'Medical Supplies', parentId: 'clinical-expense', level: 2, accountClass: 'Expense', kind: 'Posting', balance: 48600000, normalBalance: 'Debit', currency: 'PKR', active: true, mandatoryDimension: 'Cost center' },
  { id: 'salaries', code: '5200', name: 'Salaries & Benefits', parentId: 'expense', level: 1, accountClass: 'Expense', kind: 'Posting', balance: 122500000, normalBalance: 'Debit', currency: 'PKR', active: true, mandatoryDimension: 'Department' },
]

export const financeTrend = [
  { month: 'Feb', revenue: 71.8, expense: 53.4, profit: 18.4 },
  { month: 'Mar', revenue: 75.2, expense: 55.1, profit: 20.1 },
  { month: 'Apr', revenue: 69.7, expense: 57.6, profit: 12.1 },
  { month: 'May', revenue: 79.5, expense: 58.8, profit: 20.7 },
  { month: 'Jun', revenue: 83.6, expense: 61.4, profit: 22.2 },
  { month: 'Jul', revenue: 87.2, expense: 63.1, profit: 24.1 },
]

export const budgetActual = [
  { department: 'Cardiology', budget: 12.4, actual: 11.8 },
  { department: 'Emergency', budget: 9.2, actual: 9.7 },
  { department: 'Surgery', budget: 10.1, actual: 9.5 },
  { department: 'Pharmacy', budget: 7.4, actual: 7.9 },
  { department: 'Facilities', budget: 5.8, actual: 5.1 },
]

export const expenseHeads = [
  { name: 'Salaries & benefits', amount: 22.6, color: '#0d9488' },
  { name: 'Medical supplies', amount: 9.8, color: '#0ea5e9' },
  { name: 'Utilities & facilities', amount: 6.2, color: '#f59e0b' },
  { name: 'Professional fees', amount: 5.6, color: '#8b5cf6' },
]

export const vouchers: VoucherRegisterItem[] = [
  { number: 'JV-2026-0719-0048', type: 'Journal', date: '19 Jul 2026', narration: 'Accrual — clinical consultant fees', source: 'Manual journal', amount: 1840000, status: 'Pending approval', owner: 'A. Mahmood' },
  { number: 'BPV-2026-0719-0131', type: 'Bank payment', date: '19 Jul 2026', narration: 'Medical oxygen supplier settlement', source: 'Procurement', amount: 726500, status: 'Posted', owner: 'System' },
  { number: 'CRV-2026-0718-0287', type: 'Cash receipt', date: '18 Jul 2026', narration: 'OPD collection batch — Counter 01', source: 'Reception', amount: 385000, status: 'Posted', owner: 'S. Iqbal' },
  { number: 'JV-2026-0718-0043', type: 'Journal', date: '18 Jul 2026', narration: 'Monthly depreciation — imaging equipment', source: 'Fixed assets', amount: 920000, status: 'Draft', owner: 'A. Mahmood' },
]

export const fiscalPeriods = [
  { period: 'Jul 2026', status: 'Open', close: '31 Jul 2026', vouchers: 1482, owner: 'Finance Operations' },
  { period: 'Jun 2026', status: 'Closed', close: '30 Jun 2026', vouchers: 1641, owner: 'Financial Controller' },
  { period: 'May 2026', status: 'Closed', close: '31 May 2026', vouchers: 1598, owner: 'Financial Controller' },
]

export const reportCatalog = [
  { name: 'Trial Balance', description: 'Opening, period and closing balances by account hierarchy.', category: 'Core financials' },
  { name: 'General Ledger', description: 'Detailed or summary account movements with running balance.', category: 'Core financials' },
  { name: 'Income Statement', description: 'IFRS-aligned P&L with comparative periods and department views.', category: 'Financial statements' },
  { name: 'Balance Sheet', description: 'Statement of financial position with group drill-down.', category: 'Financial statements' },
  { name: 'Cash Flow Statement', description: 'Operating, investing and financing cash movements.', category: 'Financial statements' },
  { name: 'Budget vs Actual', description: 'Department, project and cost-center variance analysis.', category: 'Analysis' },
]
